import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import {
  MdApartment,
  MdEventAvailable,
  MdGroup,
  MdPayments,
} from "react-icons/md";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import Card from "components/card/Card.js";
import { useAuth } from "contexts/AuthContext";
import { useAdminOverview } from "api/admin";

const formatNaira = (value?: number | null): string => `₦${(value ?? 0).toLocaleString()}`;

export default function Overview(): JSX.Element {
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const headingColor = useColorModeValue("secondaryGray.900", "white");
  const bodyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const { user } = useAuth();
  const greetingName = user?.fullName?.split(" ")[0] || "Admin";

  const { data: overview, isLoading, isError } = useAdminOverview();

  if (isLoading) {
    return (
      <Center pt={{ base: "130px", md: "80px", xl: "80px" }} minH="50vh">
        <Spinner size="xl" color={brandColor} />
      </Center>
    );
  }

  if (isError || !overview) {
    return (
      <Center pt={{ base: "130px", md: "80px", xl: "80px" }} minH="50vh">
        <Text color="red.500">Failed to load overview data.</Text>
      </Center>
    );
  }

  const stats = [
    { name: "Total Users", icon: MdGroup, value: overview.totalUsers },
    { name: "Total Vendors", icon: MdGroup, value: overview.totalVendors },
    { name: "Verified Properties", icon: MdApartment, value: overview.properties?.verified || 0 },
    { name: "Pending Properties", icon: MdApartment, value: overview.properties?.pending || 0 },
    { name: "Total Bookings", icon: MdEventAvailable, value: overview.bookings?.total || 0 },
    { name: "Total Revenue", icon: MdPayments, value: formatNaira(overview.totalRevenue || overview.revenue || 0) },
    { name: "Pending Payouts", icon: MdPayments, value: formatNaira(overview.payoutLiability?.pendingPayoutsTotal || 0) },
    { name: "Locked Balances", icon: MdPayments, value: formatNaira(overview.payoutLiability?.lockedBalancesTotal || 0) },
    { name: "Refunds", icon: MdPayments, value: formatNaira(overview.refundsTotal || 0) },
    { name: "Cancellation Rate", icon: MdEventAvailable, value: `${((overview.cancellationRate || 0) * 100).toFixed(1)}%` },
  ];

  const series = overview.series || [];
  const chartOptions = {
    chart: { toolbar: { show: false }, fontFamily: "DM Sans, sans-serif" },
    colors: ["#FAB643", "#131313", "#6B7280"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" as const, width: 3 },
    xaxis: { categories: series.map((item) => item.label) },
    grid: { borderColor: "#E2E8F0" },
  };

  const statusEntries = Object.entries(overview.bookingsByStatus || overview.bookings?.byStatus || {});

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p={{ base: "24px", md: "32px" }} mb="20px">
        <Heading color={headingColor} fontSize={{ base: "2xl", md: "3xl" }} mb="8px">
          Welcome back, {greetingName}
        </Heading>
        <Text color={bodyColor} fontSize="md" maxW="720px">
          Alcazar admin overview. Live metrics for users, vendors, properties,
          bookings, and revenue.
        </Text>
      </Card>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="20px" mb="20px">
        {stats.map((stat) => (
          <MiniStatistics
            key={stat.name}
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={
                  <Flex align="center" justify="center" w="32px" h="32px" color={brandColor}>
                    <stat.icon size={28} />
                  </Flex>
                }
              />
            }
            name={stat.name}
            value={stat.value.toString()}
          />
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px" mb="20px">
        <Card p="20px">
          <Heading color={headingColor} fontSize="lg" mb="16px">Signups & Bookings</Heading>
          {series.length ? (
            <Chart
              options={chartOptions}
              series={[
                { name: "New users", data: series.map((item) => item.users) },
                { name: "Bookings", data: series.map((item) => item.bookings) },
              ]}
              type="line"
              height={320}
            />
          ) : <Text color={bodyColor}>No series data available.</Text>}
        </Card>

        <Card p="20px">
          <Heading color={headingColor} fontSize="lg" mb="16px">Successful Revenue</Heading>
          {series.length ? (
            <Chart
              options={{ ...chartOptions, yaxis: { labels: { formatter: (value: number) => `₦${value.toLocaleString()}` } } }}
              series={[{ name: "Revenue", data: series.map((item) => item.revenue) }]}
              type="bar"
              height={320}
            />
          ) : <Text color={bodyColor}>No revenue data available.</Text>}
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px">
        <Card p="20px">
          <Heading color={headingColor} fontSize="lg" mb="16px">Bookings by Status</Heading>
          {statusEntries.length ? (
            <Flex gap="10px" wrap="wrap">
              {statusEntries.map(([status, count]) => (
                <Badge key={status} bg="#FAB643" color="#131313" px="10px" py="6px" borderRadius="full">
                  {status.replace(/_/g, " ")}: {count}
                </Badge>
              ))}
            </Flex>
          ) : <Text color={bodyColor}>No booking status data.</Text>}
        </Card>

        <Card p="20px">
          <Heading color={headingColor} fontSize="lg" mb="16px">Top Properties by Revenue</Heading>
          {overview.topProperties?.length ? (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Property</Th>
                    <Th isNumeric>Revenue</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {overview.topProperties.map((property) => (
                    <Tr key={property.id}>
                      <Td fontWeight="600" color={headingColor}>{property.name}</Td>
                      <Td isNumeric>{formatNaira(property.total)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : <Text color={bodyColor}>No successful property revenue yet.</Text>}
        </Card>
      </SimpleGrid>
    </Box>
  );
}
